// Sessions and tokens (FR-SES, FR-QUE). Token labels carry the device prefix so
// two reception devices can issue offline without collisions (FR-QUE-5).
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import type { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { newId, nowIso } from '@/lib/db/db';
import type { SessionRow, TokenRow } from '@/lib/db/schema';

export interface QCtx { db: AbstractPowerSyncDatabase; crypto: ClinicCrypto; clinicId: string; deviceId: string | null; userId: string | null; tokenPrefix: string }

export type TokenStatus = 'waiting' | 'called' | 'in_consultation' | 'done' | 'skipped' | 'no_show';

export function todayLocal(): string {
  // Asia/Yangon business date
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Yangon', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export async function todaysSessions(db: AbstractPowerSyncDatabase, clinicId: string) {
  return db.getAll<SessionRow & { doctor_name_en: string | null; doctor_name_my: string | null; waiting: number }>(
    `select s.*, d.name_en as doctor_name_en, d.name_my as doctor_name_my,
            (select count(*) from tokens t where t.session_id = s.id and t.status in ('waiting','called')) as waiting
     from sessions s join doctors d on d.id = s.doctor_id
     where s.clinic_id = ? and s.session_date = ? and s.status <> 'cancelled' order by s.start_time`, [clinicId, todayLocal()]);
}

export async function createSession(ctx: QCtx, input: { doctor_id: string; session_date: string; start_time: string; end_time: string; room?: string | null; open?: boolean }): Promise<string> {
  const id = newId(); const ts = nowIso();
  await ctx.db.execute(
    `insert into sessions (id, clinic_id, doctor_id, session_date, start_time, end_time, room, status, opened_at, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, input.doctor_id, input.session_date, input.start_time, input.end_time, input.room ?? null, input.open ? 'open' : 'planned', input.open ? ts : null, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

export async function setSessionStatus(ctx: QCtx, sessionId: string, status: SessionRow['status']) {
  const ts = nowIso();
  await ctx.db.execute(`update sessions set status = ?, opened_at = case when ? = 'open' and opened_at is null then ? else opened_at end, closed_at = case when ? = 'closed' then ? else closed_at end, updated_at = ?, updated_by = ? where id = ?`,
    [status, status, ts, status, ts, ts, ctx.userId, sessionId]);
}

/** A token that is still in play: waiting, called, or being seen. */
export interface ActiveToken { id: string; token_label: string; session_id: string; status: TokenStatus; doctor_name_en: string | null; doctor_name_my: string | null }

/** Tokens the patient already holds today, across all of today's sessions. */
export async function activeTokensToday(db: AbstractPowerSyncDatabase, patientId: string): Promise<ActiveToken[]> {
  return db.getAll<ActiveToken>(
    `select t.id, t.token_label, t.session_id, t.status, d.name_en as doctor_name_en, d.name_my as doctor_name_my
     from tokens t join sessions s on s.id = t.session_id join doctors d on d.id = s.doctor_id
     where t.patient_id = ? and s.session_date = ? and t.status in ('waiting','called','in_consultation')
     order by t.issued_at`, [patientId, todayLocal()]);
}

/**
 * Issue a queue number.
 * A patient may legitimately see two doctors in one day, so a second token for a
 * different session is allowed. A second token for the *same* session is almost
 * always a double tap, so it returns the existing one instead.
 */
export async function issueToken(ctx: QCtx, input: { session_id: string; patient_id: string; priority?: boolean; priority_reason?: string | null }): Promise<{ id: string; label: string; existing?: boolean }> {
  const already = await ctx.db.getOptional<{ id: string; token_label: string }>(
    `select id, token_label from tokens where session_id = ? and patient_id = ? and status in ('waiting','called','in_consultation') limit 1`,
    [input.session_id, input.patient_id]);
  if (already) return { id: already.id, label: already.token_label, existing: true };

  const id = newId(); const ts = nowIso();
  // Next number for this device prefix within the session.
  const row = await ctx.db.getOptional<{ n: number | null }>(`select max(token_no) as n from tokens where session_id = ? and token_label like ?`, [input.session_id, `${ctx.tokenPrefix}%`]);
  const tokenNo = (row?.n ?? 0) + 1;
  const label = `${ctx.tokenPrefix}${tokenNo}`;
  const enc = input.priority && input.priority_reason ? await ctx.crypto.encrypt(id, { priority_reason: input.priority_reason }) : null;
  await ctx.db.execute(
    `insert into tokens (id, clinic_id, session_id, patient_id, token_no, token_label, priority, enc, status, issued_at, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, 'waiting', ?, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, input.session_id, input.patient_id, tokenNo, label, input.priority ? 1 : 0, enc, ts, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return { id, label };
}

export async function setTokenStatus(ctx: QCtx, tokenId: string, status: TokenStatus) {
  const ts = nowIso();
  const col = status === 'called' ? 'called_at' : status === 'in_consultation' ? 'started_at' : status === 'done' ? 'finished_at' : null;
  await ctx.db.execute(
    `update tokens set status = ?, ${col ? `${col} = ?,` : ''} updated_at = ?, updated_by = ? where id = ?`,
    col ? [status, ts, ts, ctx.userId, tokenId] : [status, ts, ctx.userId, tokenId]);
}

/** Doctor's "call next": the first priority token, else the lowest waiting number; marks it called. */
export async function callNext(ctx: QCtx, sessionId: string): Promise<TokenRow | null> {
  const next = await ctx.db.getOptional<TokenRow>(
    `select * from tokens where session_id = ? and status = 'waiting' order by priority desc, issued_at asc limit 1`, [sessionId]);
  if (!next) return null;
  await setTokenStatus(ctx, next.id, 'called');
  return next;
}

export async function patientVisitCount(db: AbstractPowerSyncDatabase, patientId: string): Promise<{ visits: number; last: string | null }> {
  const r = await db.getOptional<{ visits: number; last: string | null }>(`select count(*) as visits, max(issued_at) as last from tokens where patient_id = ? and status = 'done'`, [patientId]);
  return r ?? { visits: 0, last: null };
}
